import { zodResolver } from "@hookform/resolvers/zod"
import * as Sentry from "@sentry/react"
import { Button } from "design-system/atoms/Button"
import { Slider } from "design-system/atoms/Slider"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { CONSTANTS } from "lib/constants"
import { getErrorTranslations } from "lib/i18n"
import { Trash } from "lucide-react"
import {
  FrontendGenerateSizeChartImageSchema,
  GenerateSizeChartImagePayload,
  useShopifyGenerateSizeChartImageMutation,
} from "modules/settings/hooks/shopify/useShopifyGenerateSizeChartImageMutation"
import { Loading } from "modules/shared/components/Loading"
import {
  ChangeEvent,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ConvertSizeChartResponseData } from "schema"
import { z } from "zod"

type ConvertSizeChartResult = z.infer<typeof ConvertSizeChartResponseData>

type Point = {
  x: number
  y: number
}

type SizeChartImageDimension = {
  id: string
  text: string
  start: Point
  end: Point
  guide: Point
}

type ImageTransform = {
  offset: Point
  scale: number
}

export type GeneratedSizeChartImage = {
  base64Image: string | null
  dimensions: SizeChartImageDimension[]
  imageTransform?: ImageTransform
  sourceImage: string
  url: string
}

type Props = {
  generatedImage: GeneratedSizeChartImage | null
  isOpen: boolean
  onGeneratedImageChange: (image: GeneratedSizeChartImage | null) => void
  result: ConvertSizeChartResult | null
}

const CANVAS_DIMENSION_FONT = "600 28px sans-serif"
const CANVAS_DIMENSION_LABEL_HEIGHT = 42
const CANVAS_DIMENSION_LABEL_PADDING_X = 18
const CANVAS_DIMENSION_LABEL_RADIUS = 8
const CANVAS_DIMENSION_HIT_DISTANCE = 12
const CANVAS_DIMENSION_ARROW_SIZE = 12
const CANVAS_DIMENSION_EXTENSION_GAP = 6
const CANVAS_DIMENSION_EXTENSION_OVERHANG = 10
const CANVAS_DIMENSION_LABEL_GAP = 14
const CANVAS_IMAGE_SCALE = 0.8
const CANVAS_IMAGE_MIN_SCALE = 0.4
const CANVAS_IMAGE_MAX_SCALE = 1.6
const CANVAS_IMAGE_SCALE_STEP = 0.05
const CANVAS_EXPORT_MAX_BYTES = 500 * 1024
const CANVAS_EXPORT_MIME_TYPE = "image/jpeg"
const CANVAS_EXPORT_QUALITY_MAX = 0.92
const CANVAS_EXPORT_QUALITY_MIN = 0.45
const CANVAS_EXPORT_QUALITY_STEP = 0.07
const CANVAS_EXPORT_SCALE_STEP = 0.9

export function ConvertSizeChartGenerateImageForm({
  generatedImage,
  isOpen,
  onGeneratedImageChange,
  result,
}: Props) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const columns = result?.result.columns
  const variables = useMemo(
    () => columns?.map((column) => column.trim()).filter(Boolean) ?? [],
    [columns],
  )
  const form = useForm<GenerateSizeChartImagePayload>({
    resolver: zodResolver(FrontendGenerateSizeChartImageSchema),
    mode: "onChange",
    defaultValues: {
      variables,
    },
  })
  const { mutateAsync: generateImage, isPending: isGenerating } =
    useShopifyGenerateSizeChartImageMutation()

  useEffect(() => {
    form.setValue("variables", variables, { shouldValidate: true })
  }, [form, variables])

  useEffect(() => {
    if (!isOpen) {
      form.reset({ variables: [] })
      onGeneratedImageChange(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }, [form, isOpen, onGeneratedImageChange])

  if (!result) return null

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const document = e.target.files
    if (!document || document.length === 0) return

    form.setValue("document", document, { shouldValidate: true, shouldDirty: true })
    onGeneratedImageChange(null)
  }

  const onSubmit = (data: GenerateSizeChartImagePayload) => {
    generateImage({ ...data, variables })
      .then((response) => {
        onGeneratedImageChange({
          base64Image: null,
          dimensions: [],
          sourceImage: response.base64Image,
          url: response.url,
        })
      })
      .catch(() => onGeneratedImageChange(null))
  }

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex min-w-0 flex-col gap-4"
      errorTranslations={getErrorTranslations()}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <FormField
          control={form.control}
          name="document"
          render={() => (
            <FormItem className="min-w-0 flex-1">
              <FormLabel>
                {t("settings.integrations.shopify.convertSizeChart.form.productImage")}
              </FormLabel>
              <FormControl>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={CONSTANTS.acceptedImageTypes.join(",")}
                  onChange={handleFileChange}
                  className={
                    "block w-full cursor-pointer text-sm text-muted-foreground " +
                    "file:mr-4 file:rounded-md file:border-0 file:bg-muted " +
                    "file:px-4 file:py-2 file:text-sm file:font-medium " +
                    "file:text-foreground hover:file:bg-muted/80"
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={isGenerating || !form.formState.isValid || variables.length === 0}
          className="w-full sm:w-auto"
        >
          {t("settings.integrations.shopify.convertSizeChart.form.generate")}
        </Button>
      </div>
      {isGenerating && <Loading />}
      {!isGenerating && generatedImage && (
        <SizeChartImageCanvas
          imageUrl={generatedImage.sourceImage}
          initialImageTransform={generatedImage.imageTransform}
          dimensions={generatedImage.dimensions ?? []}
          onBase64ImageChange={(base64Image) => {
            onGeneratedImageChange({
              ...generatedImage,
              base64Image,
              dimensions: generatedImage.dimensions ?? [],
            })
          }}
          onDimensionsChange={(dimensions, imageTransform) => {
            onGeneratedImageChange({
              ...generatedImage,
              dimensions,
              imageTransform: imageTransform ?? generatedImage.imageTransform,
            })
          }}
          onImageTransformChange={(imageTransform) => {
            onGeneratedImageChange({ ...generatedImage, imageTransform })
          }}
          variables={variables}
          canvasLabel={t("settings.integrations.shopify.convertSizeChart.form.canvas")}
          labelsLabel={t("settings.integrations.shopify.convertSizeChart.form.labels")}
          dimensionsLabel={t("settings.integrations.shopify.convertSizeChart.form.dimensions")}
          removeLabelLabel={t("settings.integrations.shopify.convertSizeChart.form.removeLabel")}
          clearLabelsLabel={t("settings.integrations.shopify.convertSizeChart.form.clearLabels")}
          zoomLabel={t("settings.integrations.shopify.convertSizeChart.form.zoom")}
        />
      )}
    </Form>
  )
}

type SizeChartImageCanvasProps = {
  dimensions: SizeChartImageDimension[]
  imageUrl: string
  initialImageTransform?: ImageTransform
  onBase64ImageChange: (base64Image: string | null) => void
  onDimensionsChange: (
    dimensions: SizeChartImageDimension[],
    imageTransform?: ImageTransform,
  ) => void
  onImageTransformChange: (imageTransform: ImageTransform) => void
  variables: string[]
  canvasLabel: string
  labelsLabel: string
  dimensionsLabel: string
  removeLabelLabel: string
  clearLabelsLabel: string
  zoomLabel: string
}

function SizeChartImageCanvas({
  dimensions,
  imageUrl,
  initialImageTransform,
  onBase64ImageChange,
  onDimensionsChange,
  onImageTransformChange,
  variables,
  canvasLabel,
  labelsLabel,
  dimensionsLabel,
  removeLabelLabel,
  clearLabelsLabel,
  zoomLabel,
}: SizeChartImageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const initialImageTransformRef = useRef(initialImageTransform)
  const onImageTransformChangeRef = useRef(onImageTransformChange)
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [imageTransform, setImageTransform] = useState<ImageTransform>({
    offset: { x: 0, y: 0 },
    scale: CANVAS_IMAGE_SCALE,
  })
  const [activeDimensionId, setActiveDimensionId] = useState<string | null>(null)
  const [selectedVariable, setSelectedVariable] = useState<string | null>(null)
  const [pendingStartPoint, setPendingStartPoint] = useState<Point | null>(null)
  const [pendingEndPoint, setPendingEndPoint] = useState<Point | null>(null)
  const [previewPoint, setPreviewPoint] = useState<Point | null>(null)
  const [imageDragStart, setImageDragStart] = useState<{
    offset: Point
    point: Point
  } | null>(null)

  useEffect(() => {
    initialImageTransformRef.current = initialImageTransform
    onImageTransformChangeRef.current = onImageTransformChange
  })

  useEffect(() => {
    let isCurrentImage = true
    const nextImage = new Image()
    if (!imageUrl.startsWith("data:")) {
      nextImage.crossOrigin = "anonymous"
    }

    nextImage.onload = () => {
      if (!isCurrentImage) return

      setImage(nextImage)
      setSelectedVariable(null)
      setActiveDimensionId(null)
      setPendingStartPoint(null)
      setPendingEndPoint(null)
      setPreviewPoint(null)
      const nextTransform = initialImageTransformRef.current ?? {
        offset: { x: 0, y: 0 },
        scale: CANVAS_IMAGE_SCALE,
      }

      setImageTransform(nextTransform)
      onImageTransformChangeRef.current(nextTransform)
      setImageDragStart(null)
    }
    nextImage.onerror = () => {
      if (!isCurrentImage) return

      setImage(null)
      setSelectedVariable(null)
      setActiveDimensionId(null)
      setPendingStartPoint(null)
      setPendingEndPoint(null)
      setPreviewPoint(null)
      setImageDragStart(null)
    }
    nextImage.src = imageUrl

    return () => {
      isCurrentImage = false
    }
  }, [imageUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image) return

    canvas.width = image.naturalWidth || image.width
    canvas.height = image.naturalHeight || image.height

    const context = canvas.getContext("2d")
    if (!context) return

    context.clearRect(0, 0, canvas.width, canvas.height)
    drawCanvasImage(context, image, canvas, imageTransform)

    dimensions.forEach((dimension) => {
      drawCanvasDimension(context, dimension, dimension.id === activeDimensionId)
    })

    if (pendingStartPoint && pendingEndPoint && selectedVariable && previewPoint) {
      drawCanvasDimension(
        context,
        {
          end: pendingEndPoint,
          guide: previewPoint,
          id: "preview",
          start: pendingStartPoint,
          text: selectedVariable,
        },
        true,
      )
    } else if (pendingStartPoint && selectedVariable && previewPoint) {
      drawCanvasDimension(
        context,
        {
          end: previewPoint,
          guide: pendingStartPoint,
          id: "preview",
          start: pendingStartPoint,
          text: selectedVariable,
        },
        true,
      )
    } else if (pendingStartPoint && pendingEndPoint) {
      drawPendingDimension(context, pendingStartPoint, pendingEndPoint)
    } else if (pendingStartPoint) {
      drawPendingStartPoint(context, pendingStartPoint)
    }

    if (!pendingStartPoint && !pendingEndPoint && !previewPoint) {
      onBase64ImageChange(getCommittedCanvasBase64Image(image, dimensions, canvas, imageTransform))
    }
  }, [
    activeDimensionId,
    dimensions,
    image,
    imageTransform,
    onBase64ImageChange,
    pendingEndPoint,
    pendingStartPoint,
    previewPoint,
    selectedVariable,
  ])

  const getPointerPoint = (event: ReactPointerEvent<HTMLCanvasElement>): Point | null => {
    const canvas = canvasRef.current
    if (!canvas) return null

    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null

    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = getPointerPoint(event)
    if (!point) return

    if (selectedVariable) {
      if (!pendingStartPoint) {
        setPendingStartPoint(point)
        setPreviewPoint(null)
        setActiveDimensionId(null)
        return
      }

      if (!pendingEndPoint) {
        const endPoint = event.shiftKey
          ? getConstrainedDimensionPoint(pendingStartPoint, point)
          : point

        setPendingEndPoint(endPoint)
        setPreviewPoint(endPoint)
        return
      }

      const nextDimension = {
        end: pendingEndPoint,
        guide: point,
        id: crypto.randomUUID(),
        start: pendingStartPoint,
        text: selectedVariable,
      }

      onDimensionsChange([...dimensions, nextDimension])
      setActiveDimensionId(nextDimension.id)
      setPendingEndPoint(null)
      setPendingStartPoint(null)
      setPreviewPoint(null)
      setSelectedVariable(null)
      return
    }

    const selectedDimension = selectDimensionAtPoint(point)
    if (selectedDimension) return

    event.currentTarget.setPointerCapture(event.pointerId)
    setImageDragStart({ offset: imageTransform.offset, point })
  }

  const selectDimensionAtPoint = (point: Point) => {
    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (!context) return false

    const dimension = [...dimensions]
      .reverse()
      .find((candidate) => isPointInsideDimension(context, candidate, point))

    setActiveDimensionId(dimension?.id ?? null)
    return Boolean(dimension)
  }

  const handleVariableClick = (variable: string) => {
    setSelectedVariable((currentVariable) => (currentVariable === variable ? null : variable))
    setPendingEndPoint(null)
    setPendingStartPoint(null)
    setPreviewPoint(null)
    setActiveDimensionId(null)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = getPointerPoint(event)
    if (!point) return

    if (imageDragStart) {
      const delta = {
        x: point.x - imageDragStart.point.x,
        y: point.y - imageDragStart.point.y,
      }
      const nextTransform = {
        ...imageTransform,
        offset: addPoints(imageDragStart.offset, delta),
      }

      setImageTransform(nextTransform)
      onImageTransformChange(nextTransform)

      if (dimensions.length > 0) {
        onDimensionsChange(
          dimensions.map((dimension) => translateDimension(dimension, delta)),
          nextTransform,
        )
        setImageDragStart({ offset: addPoints(imageDragStart.offset, delta), point })
      }

      return
    }

    if (!selectedVariable || !pendingStartPoint) return

    if (!pendingEndPoint && event.shiftKey) {
      setPreviewPoint(getConstrainedDimensionPoint(pendingStartPoint, point))
      return
    }

    setPreviewPoint(point)
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (imageDragStart && event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    setImageDragStart(null)
  }

  const handleZoomChange = ([nextScale]: number[]) => {
    const canvas = canvasRef.current
    if (!canvas || nextScale === undefined) return

    const nextTransform = {
      ...imageTransform,
      scale: nextScale,
    }
    const center = {
      x: canvas.width / 2 + imageTransform.offset.x,
      y: canvas.height / 2 + imageTransform.offset.y,
    }
    const scaleRatio = nextScale / imageTransform.scale

    setImageTransform(nextTransform)
    onImageTransformChange(nextTransform)

    if (dimensions.length > 0) {
      onDimensionsChange(
        dimensions.map((dimension) => scaleDimension(dimension, center, scaleRatio)),
        nextTransform,
      )
    }
  }

  const removeDimension = (id: string) => {
    onDimensionsChange(dimensions.filter((dimension) => dimension.id !== id))
    setActiveDimensionId((currentId) => (currentId === id ? null : currentId))
  }

  const removeActiveDimension = () => {
    if (!activeDimensionId) return
    removeDimension(activeDimensionId)
  }

  const clearDimensions = () => {
    onDimensionsChange([])
    setSelectedVariable(null)
    setActiveDimensionId(null)
    setPendingEndPoint(null)
    setPendingStartPoint(null)
    setPreviewPoint(null)
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">{labelsLabel}</div>
        <div className="flex flex-wrap gap-2">
          {variables.map((variable) => (
            <Button
              key={variable}
              type="button"
              variant={selectedVariable === variable ? "default" : "outline"}
              size="sm"
              onClick={() => handleVariableClick(variable)}
              disabled={!image}
            >
              {variable}
            </Button>
          ))}
          {activeDimensionId && (
            <Button type="button" variant="outline" size="sm" onClick={removeActiveDimension}>
              {removeLabelLabel}
            </Button>
          )}
          {dimensions.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={clearDimensions}>
              {clearLabelsLabel}
            </Button>
          )}
        </div>
      </div>
      {dimensions.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">{dimensionsLabel}</div>
          <div className="flex flex-wrap gap-2">
            {dimensions.map((dimension, index) => (
              <div
                key={dimension.id}
                className={cn(
                  "flex items-center overflow-hidden rounded-md border",
                  activeDimensionId === dimension.id && "border-primary",
                )}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveDimensionId(dimension.id)}
                  className="rounded-r-none"
                >
                  {dimension.text} {index + 1}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeDimension(dimension.id)}
                  aria-label={removeLabelLabel}
                  title={removeLabelLabel}
                  className="h-8 w-8 rounded-l-none"
                >
                  <Trash />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        className={
          "flex min-w-0 items-center justify-center overflow-auto rounded-md " +
          "border bg-border p-2"
        }
      >
        {!image && <Loading />}
        {image && (
          <canvas
            ref={canvasRef}
            aria-label={canvasLabel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className={cn(
              "h-auto max-h-[560px] max-w-full touch-none rounded-sm",
              selectedVariable ? "cursor-crosshair" : "cursor-move",
            )}
          />
        )}
      </div>
      {image && (
        <div className="flex items-center gap-3">
          <span className="w-14 shrink-0 text-sm font-medium">{zoomLabel}</span>
          <Slider
            value={[imageTransform.scale]}
            min={CANVAS_IMAGE_MIN_SCALE}
            max={CANVAS_IMAGE_MAX_SCALE}
            step={CANVAS_IMAGE_SCALE_STEP}
            onValueChange={handleZoomChange}
            aria-label={zoomLabel}
          />
          <span className="w-12 shrink-0 text-right text-sm text-muted-foreground">
            {Math.round((imageTransform.scale / CANVAS_IMAGE_SCALE) * 100)}
            {"%"}
          </span>
        </div>
      )}
    </div>
  )
}

function getCanvasBase64Image(canvas: HTMLCanvasElement) {
  try {
    return getCompressedCanvasJpeg(canvas)
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: "Failed to export size chart canvas image" },
    })
    return null
  }
}

function getCommittedCanvasBase64Image(
  image: HTMLImageElement,
  dimensions: SizeChartImageDimension[],
  sourceCanvas: HTMLCanvasElement,
  transform: ImageTransform,
) {
  const canvas = document.createElement("canvas")
  canvas.width = sourceCanvas.width
  canvas.height = sourceCanvas.height

  const context = canvas.getContext("2d")
  if (!context) return null

  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, canvas.width, canvas.height)
  drawCanvasImage(context, image, canvas, transform)
  dimensions.forEach((dimension) => {
    drawCanvasDimension(context, dimension, false)
  })

  return getCanvasBase64Image(canvas)
}

function getCompressedCanvasJpeg(sourceCanvas: HTMLCanvasElement) {
  let canvas = sourceCanvas
  let bestImage = ""

  while (canvas.width > 1 && canvas.height > 1) {
    for (
      let quality = CANVAS_EXPORT_QUALITY_MAX;
      quality >= CANVAS_EXPORT_QUALITY_MIN;
      quality -= CANVAS_EXPORT_QUALITY_STEP
    ) {
      const image = canvas.toDataURL(CANVAS_EXPORT_MIME_TYPE, quality)
      bestImage = image

      if (getDataUrlByteSize(image) <= CANVAS_EXPORT_MAX_BYTES) {
        return image
      }
    }

    canvas = resizeCanvas(canvas, CANVAS_EXPORT_SCALE_STEP)
  }

  return bestImage || canvas.toDataURL(CANVAS_EXPORT_MIME_TYPE, CANVAS_EXPORT_QUALITY_MIN)
}

function getDataUrlByteSize(dataUrl: string) {
  const base64 = dataUrl.split(",")[1] ?? ""
  return Math.floor((base64.length * 3) / 4)
}

function resizeCanvas(sourceCanvas: HTMLCanvasElement, scale: number) {
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.floor(sourceCanvas.width * scale))
  canvas.height = Math.max(1, Math.floor(sourceCanvas.height * scale))

  const context = canvas.getContext("2d")
  if (!context) return sourceCanvas

  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height)

  return canvas
}

function drawCanvasImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  transform: ImageTransform,
) {
  const width = canvas.width * transform.scale
  const height = canvas.height * transform.scale
  const x = (canvas.width - width) / 2 + transform.offset.x
  const y = (canvas.height - height) / 2 + transform.offset.y

  context.fillStyle = getImageTopLeftPixelColor(image)
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image, x, y, width, height)
}

function translateDimension(dimension: SizeChartImageDimension, delta: Point) {
  return {
    ...dimension,
    end: addPoints(dimension.end, delta),
    guide: addPoints(dimension.guide, delta),
    start: addPoints(dimension.start, delta),
  }
}

function scalePointFromCenter(point: Point, center: Point, scale: number) {
  return {
    x: center.x + (point.x - center.x) * scale,
    y: center.y + (point.y - center.y) * scale,
  }
}

function scaleDimension(dimension: SizeChartImageDimension, center: Point, scale: number) {
  return {
    ...dimension,
    end: scalePointFromCenter(dimension.end, center, scale),
    guide: scalePointFromCenter(dimension.guide, center, scale),
    start: scalePointFromCenter(dimension.start, center, scale),
  }
}

function getImageTopLeftPixelColor(image: HTMLImageElement) {
  const canvas = document.createElement("canvas")
  canvas.width = 1
  canvas.height = 1

  const context = canvas.getContext("2d")
  if (!context) return "#ffffff"

  try {
    context.drawImage(image, 0, 0, 1, 1, 0, 0, 1, 1)
    const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data

    return `rgba(${red}, ${green}, ${blue}, ${(alpha ?? 255) / 255})`
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: "Failed to read size chart image background color" },
    })
    return "#ffffff"
  }
}

function getDimensionLinePoints(dimension: SizeChartImageDimension) {
  const vector = getDimensionVector(dimension.start, dimension.end)
  const offset = getDimensionGuideOffset(dimension.start, dimension.guide, vector.normal)

  return {
    end: addPoints(dimension.end, scalePoint(vector.normal, offset)),
    start: addPoints(dimension.start, scalePoint(vector.normal, offset)),
  }
}

function getDimensionMidpoint(dimension: SizeChartImageDimension): Point {
  const linePoints = getDimensionLinePoints(dimension)

  return {
    x: (linePoints.start.x + linePoints.end.x) / 2,
    y: (linePoints.start.y + linePoints.end.y) / 2,
  }
}

function getDimensionLabelCenter(dimension: SizeChartImageDimension): Point {
  const linePoints = getDimensionLinePoints(dimension)
  const midpoint = getDimensionMidpoint(dimension)
  const baseVector = getDimensionVector(dimension.start, dimension.end)
  const lineVector = getDimensionVector(linePoints.start, linePoints.end)
  const guideOffset = getDimensionGuideOffset(dimension.start, dimension.guide, baseVector.normal)
  const labelOffset = CANVAS_DIMENSION_LABEL_HEIGHT / 2 + CANVAS_DIMENSION_LABEL_GAP
  const labelSide = guideOffset < 0 ? -1 : 1

  return addPoints(midpoint, scalePoint(lineVector.normal, labelOffset * labelSide))
}

function getDimensionVector(start: Point, end: Point) {
  const x = end.x - start.x
  const y = end.y - start.y
  const length = Math.hypot(x, y) || 1
  const unit = { x: x / length, y: y / length }

  return {
    length,
    normal: { x: -unit.y, y: unit.x },
    unit,
  }
}

function getDimensionGuideOffset(start: Point, guide: Point, normal: Point) {
  return (guide.x - start.x) * normal.x + (guide.y - start.y) * normal.y
}

function addPoints(a: Point, b: Point) {
  return { x: a.x + b.x, y: a.y + b.y }
}

function scalePoint(point: Point, scale: number) {
  return { x: point.x * scale, y: point.y * scale }
}

function getConstrainedDimensionPoint(start: Point, point: Point) {
  const delta = {
    x: point.x - start.x,
    y: point.y - start.y,
  }
  const distance = Math.hypot(delta.x, delta.y)

  if (distance === 0) return point

  const angle = Math.atan2(delta.y, delta.x)
  const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4)

  return {
    x: start.x + Math.cos(snappedAngle) * distance,
    y: start.y + Math.sin(snappedAngle) * distance,
  }
}

function getReadableDimensionAngle(unit: Point) {
  const angle = Math.atan2(unit.y, unit.x)

  if (angle > Math.PI / 2) return angle - Math.PI
  if (angle < -Math.PI / 2) return angle + Math.PI

  return angle
}

function getCanvasDimensionLabelBounds(
  context: CanvasRenderingContext2D,
  dimension: SizeChartImageDimension,
) {
  const center = getDimensionLabelCenter(dimension)

  context.font = CANVAS_DIMENSION_FONT
  const width = context.measureText(dimension.text).width + CANVAS_DIMENSION_LABEL_PADDING_X * 2
  const height = CANVAS_DIMENSION_LABEL_HEIGHT

  return {
    height,
    left: center.x - width / 2,
    top: center.y - height / 2,
    width,
  }
}

function isPointInsideDimension(
  context: CanvasRenderingContext2D,
  dimension: SizeChartImageDimension,
  point: Point,
) {
  const bounds = getCanvasDimensionLabelBounds(context, dimension)
  const isInsideLabel =
    point.x >= bounds.left &&
    point.x <= bounds.left + bounds.width &&
    point.y >= bounds.top &&
    point.y <= bounds.top + bounds.height

  if (isInsideLabel) return true

  const linePoints = getDimensionLinePoints(dimension)

  const endpointDistance = Math.min(
    getDistanceBetweenPoints(point, linePoints.start),
    getDistanceBetweenPoints(point, linePoints.end),
  )

  return (
    endpointDistance <= CANVAS_DIMENSION_HIT_DISTANCE ||
    getDistanceToSegment(point, linePoints.start, linePoints.end) <=
      CANVAS_DIMENSION_HIT_DISTANCE ||
    getDistanceToSegment(point, dimension.start, linePoints.start) <=
      CANVAS_DIMENSION_HIT_DISTANCE ||
    getDistanceToSegment(point, dimension.end, linePoints.end) <= CANVAS_DIMENSION_HIT_DISTANCE
  )
}

function getDistanceBetweenPoints(a: Point, b: Point) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function getDistanceToSegment(point: Point, start: Point, end: Point) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const lengthSquared = dx * dx + dy * dy

  if (lengthSquared === 0) return getDistanceBetweenPoints(point, start)

  const projection = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared
  const clampedProjection = Math.max(0, Math.min(1, projection))
  const projectedPoint = {
    x: start.x + clampedProjection * dx,
    y: start.y + clampedProjection * dy,
  }

  return getDistanceBetweenPoints(point, projectedPoint)
}

function drawCanvasDimension(
  context: CanvasRenderingContext2D,
  dimension: SizeChartImageDimension,
  isActive: boolean,
) {
  const linePoints = getDimensionLinePoints(dimension)
  const labelCenter = getDimensionLabelCenter(dimension)
  const bounds = getCanvasDimensionLabelBounds(context, dimension)
  const vector = getDimensionVector(linePoints.start, linePoints.end)
  const strokeColor = isActive ? "#111827" : "rgba(17, 24, 39, 0.65)"

  context.save()
  context.strokeStyle = strokeColor
  context.fillStyle = strokeColor
  context.lineWidth = isActive ? 3 : 2
  context.setLineDash([8, 6])

  context.beginPath()
  context.moveTo(linePoints.start.x, linePoints.start.y)
  context.lineTo(linePoints.end.x, linePoints.end.y)
  context.stroke()

  drawDimensionExtensionLine(context, dimension.start, linePoints.start)
  drawDimensionExtensionLine(context, dimension.end, linePoints.end)
  context.setLineDash([])
  drawDimensionArrowhead(context, linePoints.start, vector.unit)
  drawDimensionArrowhead(context, linePoints.end, scalePoint(vector.unit, -1))

  context.save()
  context.translate(labelCenter.x, labelCenter.y)
  context.rotate(getReadableDimensionAngle(vector.unit))
  context.fillStyle = "rgba(255, 255, 255, 0.9)"
  context.strokeStyle = strokeColor
  context.lineWidth = isActive ? 3 : 1.5
  drawRoundedRect(context, -bounds.width / 2, -bounds.height / 2, bounds.width, bounds.height)
  context.fill()
  context.stroke()

  context.fillStyle = "#111827"
  context.font = CANVAS_DIMENSION_FONT
  context.textAlign = "center"
  context.textBaseline = "middle"
  context.fillText(dimension.text, 0, 1)
  context.restore()
  context.restore()
}

function drawDimensionExtensionLine(context: CanvasRenderingContext2D, anchor: Point, line: Point) {
  const vector = getDimensionVector(anchor, line)
  const gapPoint = addPoints(anchor, scalePoint(vector.unit, CANVAS_DIMENSION_EXTENSION_GAP))
  const endPoint = addPoints(line, scalePoint(vector.unit, CANVAS_DIMENSION_EXTENSION_OVERHANG))

  context.beginPath()
  context.moveTo(gapPoint.x, gapPoint.y)
  context.lineTo(endPoint.x, endPoint.y)
  context.stroke()
}

function drawDimensionArrowhead(context: CanvasRenderingContext2D, tip: Point, direction: Point) {
  const angle = Math.atan2(direction.y, direction.x)

  context.beginPath()
  context.moveTo(tip.x, tip.y)
  context.lineTo(
    tip.x - CANVAS_DIMENSION_ARROW_SIZE * Math.cos(angle - Math.PI / 6),
    tip.y - CANVAS_DIMENSION_ARROW_SIZE * Math.sin(angle - Math.PI / 6),
  )
  context.moveTo(tip.x, tip.y)
  context.lineTo(
    tip.x - CANVAS_DIMENSION_ARROW_SIZE * Math.cos(angle + Math.PI / 6),
    tip.y - CANVAS_DIMENSION_ARROW_SIZE * Math.sin(angle + Math.PI / 6),
  )
  context.stroke()
}

function drawPendingStartPoint(context: CanvasRenderingContext2D, point: Point) {
  context.save()
  context.strokeStyle = "#111827"
  context.fillStyle = "rgba(255, 255, 255, 0.9)"
  context.lineWidth = 2
  context.beginPath()
  context.arc(point.x, point.y, 6, 0, Math.PI * 2)
  context.fill()
  context.stroke()
  context.restore()
}

function drawPendingDimension(context: CanvasRenderingContext2D, start: Point, end: Point) {
  context.save()
  context.strokeStyle = "#111827"
  context.fillStyle = "rgba(255, 255, 255, 0.9)"
  context.lineWidth = 2
  context.setLineDash([8, 6])
  context.beginPath()
  context.moveTo(start.x, start.y)
  context.lineTo(end.x, end.y)
  context.stroke()
  context.setLineDash([])
  drawPendingStartPoint(context, start)
  drawPendingStartPoint(context, end)
  context.restore()
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const radius = Math.min(CANVAS_DIMENSION_LABEL_RADIUS, width / 2, height / 2)

  context.beginPath()
  context.moveTo(x + radius, y)
  context.arcTo(x + width, y, x + width, y + height, radius)
  context.arcTo(x + width, y + height, x, y + height, radius)
  context.arcTo(x, y + height, x, y, radius)
  context.arcTo(x, y, x + width, y, radius)
  context.closePath()
}
